import axios, { AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { JwtTokenService } from "../services/jwtTokenService";

let interceptorsSetup = false;

// Helper function to get the base path from the current URL
// When app is deployed to a virtual directory like /AppointmentsTest/,
// this extracts that path to prefix all API calls
const getBasePath = (): string => {
  // Get the pathname, which includes the virtual directory if present
  const pathname = window.location.pathname;

  // For HashRouter apps, the base path is everything before index.html or the hash
  // Examples:
  // /AppointmentsTest/internal.html -> /AppointmentsTest
  // /AppointmentsTest/ -> /AppointmentsTest
  // / -> (empty)

  let basePath = pathname;

  // Remove index.html, internal.html, external.html, admin.html etc if present
  basePath = basePath.replace(/\/(internal|external|admin|index)\.html.*$/, '');

  // Remove trailing slash if present (unless it's just "/")
  if (basePath.length > 1 && basePath.endsWith('/')) {
    basePath = basePath.slice(0, -1);
  }

  // If we're at root, return empty string (no base path needed)
  if (basePath === '/' || basePath === '') {
    return '';
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('Axios Config: Detected base path:', basePath, 'from pathname:', pathname);
  }

  return basePath;
};

// Setup global axios interceptors for JWT token handling
export const setupAxiosInterceptors = () => {
  // Prevent multiple setup calls
  if (interceptorsSetup) {
    return;
  }

  // Configure axios baseURL to include the virtual directory path
  const basePath = getBasePath();
  if (basePath) {
    axios.defaults.baseURL = basePath;
    if (process.env.NODE_ENV === 'development') {
      console.log('Axios Config: Set baseURL to:', axios.defaults.baseURL);
    }
  }

  // Request interceptor to add API-JWT-TOKEN header
  axios.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {} as any;
    }
    
    // Add API-JWT-TOKEN header based on token availability and request type
    if (JwtTokenService.hasToken()) {
      const token = JwtTokenService.getToken();
      config.headers['API-JWT-TOKEN'] = token;
      
      // Debug logging (can be removed in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('JWT Token Interceptor: Adding API-JWT-TOKEN header', {
          url: config.url,
          hasToken: !!token,
          tokenLength: token ? token.length : 0
        });
      }
    } else {
      // Special case for GET /api/appsettings when no token exists
      if (config.method && config.method.toLowerCase() === 'get' && 
          config.url && config.url.includes('/api/appsettings')) {
        config.headers['API-JWT-TOKEN'] = 'FIRST-APPSETTINGS-REQUEST';
        
        // Debug logging (can be removed in production)
        if (process.env.NODE_ENV === 'development') {
          console.log('JWT Token Interceptor: Using "FIRST-APPSETTINGS-REQUEST" for first appsettings call', config.url);
        }
      } else {
        config.headers['API-JWT-TOKEN'] = '';
        
        // Debug logging (can be removed in production)
        if (process.env.NODE_ENV === 'development') {
          console.log('JWT Token Interceptor: No token found in sessionStorage, using empty value for', config.url);
        }
      }
    }
    
    return config;
  }, (error) => {
    return Promise.reject(error);
  });

  // Response interceptor to extract and save API-JWT-TOKEN
  axios.interceptors.response.use((response: AxiosResponse) => {
    // Always check for API-JWT-TOKEN in response headers
    // Check multiple possible header case variations
    const jwtToken = response.headers['API-JWT-TOKEN'] || 
                     response.headers['api-jwt-token'] || 
                     response.headers['Api-Jwt-Token'] ||
                     response.headers['API_JWT_TOKEN'] ||
                     response.headers['api_jwt_token'];
    
    // Only save to sessionStorage if a valid token is extracted
    if (jwtToken && jwtToken.trim() !== '' && 
        jwtToken !== 'FIRST-APPSETTINGS-REQUEST') {
      JwtTokenService.setToken(jwtToken);
      
      // Debug logging (can be removed in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('JWT Token Interceptor: Extracted and saved API-JWT-TOKEN from response', {
          url: response.config ? response.config.url : 'unknown',
          tokenLength: jwtToken.length,
          tokenPreview: jwtToken.substring(0, 20) + '...'
        });
      }
    } else {
      // Debug logging (can be removed in production)  
      if (process.env.NODE_ENV === 'development') {
        const availableHeaders = Object.keys(response.headers || {});
        console.log('JWT Token Interceptor: No valid API-JWT-TOKEN found in response headers', {
          url: response.config ? response.config.url : 'unknown',
          foundToken: jwtToken || 'none',
          availableHeaders: availableHeaders.filter(h => h.toLowerCase().includes('token') || h.toLowerCase().includes('jwt'))
        });
      }
    }
    
    return response;
  }, async (error) => {
    // Handle 401 Unauthorized errors - likely due to expired/invalid API-JWT-TOKEN
    if (error.response && error.response.status === 401) {
      const url = error.config ? error.config.url : 'unknown';

      // Prevent infinite retry loops by checking if this is already a retry
      if (error.config._retry) {
        return Promise.reject(error);
      }

      // If we get 401 on an API endpoint, the API-JWT-TOKEN is likely expired/invalid
      if (url && url.includes('/api/')) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('JWT Token Interceptor: 401 Unauthorized - clearing expired API-JWT-TOKEN', {
            url: url,
            hadToken: JwtTokenService.hasToken()
          });
        }

        // Mark this request as a retry
        error.config._retry = true;

        // Clear the expired/invalid token
        JwtTokenService.removeToken();

        // For appsettings requests, we can retry with FIRST-APPSETTINGS-REQUEST
        if (url.includes('/api/appsettings')) {
          if (process.env.NODE_ENV === 'development') {
            console.log('JWT Token Interceptor: Retrying appsettings request with FIRST-APPSETTINGS-REQUEST');
          }

          // Modify the config to use the special token
          error.config.headers['API-JWT-TOKEN'] = 'FIRST-APPSETTINGS-REQUEST';

          // Retry the request
          return axios.request(error.config);
        } else {
          // For other endpoints, fetch a fresh token from appsettings first
          try {
            if (process.env.NODE_ENV === 'development') {
              console.log('JWT Token Interceptor: Fetching fresh token from appsettings before retrying', url);
            }

            // Make a request to appsettings to get a fresh token
            await axios.get('/api/appsettings', {
              headers: {
                'API-JWT-TOKEN': 'FIRST-APPSETTINGS-REQUEST'
              }
            });

            // The response interceptor above will automatically save the new token
            // Now retry the original request with the fresh token
            const newToken = JwtTokenService.getToken();
            if (newToken) {
              error.config.headers['API-JWT-TOKEN'] = newToken;

              if (process.env.NODE_ENV === 'development') {
                console.log('JWT Token Interceptor: Retrying original request with fresh token', url);
              }

              return axios.request(error.config);
            }
          } catch (tokenError) {
            if (process.env.NODE_ENV === 'development') {
              console.error('JWT Token Interceptor: Failed to fetch fresh token', tokenError);
            }
            // Fall through to reject the original error
          }
        }
      }
    }

    // Also check for tokens in error responses
    if (error.response && error.response.headers) {
      const jwtToken = error.response.headers['API-JWT-TOKEN'] ||
                       error.response.headers['api-jwt-token'] ||
                       error.response.headers['Api-Jwt-Token'] ||
                       error.response.headers['API_JWT_TOKEN'] ||
                       error.response.headers['api_jwt_token'];

      if (jwtToken && jwtToken.trim() !== '' &&
          jwtToken !== 'FIRST-APPSETTINGS-REQUEST') {
        JwtTokenService.setToken(jwtToken);

        if (process.env.NODE_ENV === 'development') {
          console.log('JWT Token Interceptor: Extracted token from error response', {
            url: error.response.config ? error.response.config.url : 'unknown',
            status: error.response.status
          });
        }
      }
    }

    return Promise.reject(error);
  });

  interceptorsSetup = true;
};